import React, { lazy, Suspense } from 'react';
import { datadogRum } from '@datadog/browser-rum';

const retryCount = 1;

/**
 * Detects errors thrown when a dynamically-imported module or its CSS fails to
 * load. Vite + native ESM browsers throw a different error than the
 * webpack-era `ChunkLoadError`, and Safari/WebKit (including Chrome on iOS)
 * is especially prone to failing the speculative `<link rel="modulepreload">`
 * and `<link rel="preload" as="style">` tags Vite emits for code-split
 * chunks. When that happens, the user dead-ends on a generic error screen
 * even though a reload would recover them.
 *
 * Matches:
 *  - `ChunkLoadError` (webpack-era / @vitejs/plugin-legacy fallback)
 *  - "Importing a module script failed." (Safari / WebKit)
 *  - "Failed to fetch dynamically imported module" (Chrome / Edge)
 *  - "error loading dynamically imported module" (Firefox)
 *  - "Unable to preload CSS for ..." (Vite preloadCSS helper)
 *  - "Unexpected token '<'" SyntaxError (stale chunk returned an HTML page)
 */
export const isChunkLoadError = (error) => {
	if (!error) return false;
	if (error.name === 'ChunkLoadError') return true;
	const message = typeof error.message === 'string' ? error.message : '';
	return (
		(error.name === 'SyntaxError' && message.includes("Unexpected token '<'")) ||
		message.includes('Importing a module script failed') ||
		message.includes('Failed to fetch dynamically imported module') ||
		message.includes('error loading dynamically imported module') ||
		message.includes('Unable to preload CSS')
	);
};

const pause = (timeout) => new Promise((resolve) => setTimeout(resolve, timeout));

// Best-effort RUM instrumentation. Mirrors the pattern in fetchWithRetry.ts:
// telemetry must never reject a user-facing promise. Without this guard, a
// throwing `addAction` inside the success handler at line ~96 would reject
// the cached chain and permanently poison the recovery path this module
// is meant to fix.
const safeAddRumAction = (name, context) => {
	try {
		if (context === undefined) {
			datadogRum.addAction(name);
		} else {
			datadogRum.addAction(name, context);
		}
	} catch (_e) {
		// Observability is never worth breaking the loader for.
	}
};

export const retryAsyncFunction = async (fn, remainingRetries = retryCount + 1, interval = 750) => {
	while (remainingRetries) {
		try {
			return await fn();
		} catch (e) {
			remainingRetries -= 1;
			if (!remainingRetries) {
				throw e;
			}
			// Only sleep between actual retries — skipping the pre-throw pause
			// shaves `interval` ms off the final-failure path, which for chunk
			// loads is the time before ErrorBoundary's reload backstop fires.
			await pause(interval);
		}
	}
};

export const lazyLoad = (importFunc) => {
	const LazyLoadedComponent = lazy(() => retryAsyncFunction(importFunc));

	// eslint-disable-next-line react/display-name
	return (props) => (
		<Suspense fallback={<div></div>}>
			<LazyLoadedComponent {...props} />
		</Suspense>
	);
};

/**
 * Like `React.lazy()`, but with a `.preload()` method for speculative chunk
 * loading used by the onboarding graph to warm up the next screen.
 *
 * Differences from a naive `lazy(factory)`:
 *
 *  1. **Retry** — the raw factory goes through `retryAsyncFunction`, so a
 *     transient network blip gets one automatic retry before giving up.
 *
 *  2. **Shared cache with poison-resistance** — a successful import is cached
 *     so `.preload()` and the subsequent `React.lazy` render share the same
 *     resolved promise. On failure the cache is *cleared* instead of holding
 *     the rejected promise, so a later on-demand render isn't doomed by a
 *     speculative preload that failed while the user was still on the
 *     previous screen (STASH2026-4213).
 *
 *  3. **Speculative errors are swallowed** — `.preload()` always resolves; a
 *     failure is logged to Datadog RUM but never surfaces to callers or the
 *     `ErrorBoundary`. If the render path later also fails, the boundary's
 *     reload-once backstop still fires.
 *
 *  4. **Observability** — each lifecycle event (`attempt`, `preload_failed`,
 *     `cache_cleared`, `recovered`) is reported to Datadog RUM so we can
 *     measure how often retry/recovery actually saves a user in production.
 */
export const lazyPreload = (factory) => {
	let cachedResult = null;
	let hasFailed = false;

	const retryingFactory = () => {
		if (cachedResult) return cachedResult;
		const isRecovery = hasFailed;
		const promise = retryAsyncFunction(factory).then(
			(mod) => {
				if (isRecovery) {
					safeAddRumAction('lazyPreload.recovered');
				}
				hasFailed = false;
				cachedResult = Promise.resolve(mod);
				return mod;
			},
			(err) => {
				hasFailed = true;
				cachedResult = null;
				safeAddRumAction('lazyPreload.cache_cleared', {
					error: err && err.message,
				});
				throw err;
			}
		);
		cachedResult = promise;
		return promise;
	};

	const Component = lazy(retryingFactory);
	Component.preload = () => {
		safeAddRumAction('lazyPreload.attempt');
		return retryingFactory().catch((err) => {
			safeAddRumAction('lazyPreload.preload_failed', {
				error: err && err.message,
			});
		});
	};
	return Component;
};

export default lazyLoad;
